import { routePrefixLanguages } from "../../i18n";
import { ContentType, ContentTypePathSegment } from "../../constants";

/**
 * Derives the tracking content type from a pathname.
 *
 * Any leading language prefix (e.g. "/en/") is stripped first, then the first
 * path segment is matched:
 * - "news"               → "article"
 * - "corporate-services" → "corporate_service"
 * - "vacancies"          → "corporate_service"
 * - "settings"           → "system_page"
 * - anything else        → "content_page"
 * 
 * @param pathname - The pathname to derive the content type from.
 * @returns The derived content type, for use in the `content_type` tracking search param.
 *
 * @example
 * getContentTypeFromPath("/news/some-article")        // "article"
 * getContentTypeFromPath("/en/news/some-article")     // "article"
 * getContentTypeFromPath("/corporate-services/legal") // "corporate_service"
 * getContentTypeFromPath("/")                         // "content_page"
 */
export const getContentTypeFromPath = (pathname: string): ContentType => {
  const segments = pathname.split("/").filter(Boolean);

  // Strip a leading language prefix (e.g. "/en/...") so Dutch and English
  // paths resolve to the same content type.
  if (segments[0] && routePrefixLanguages.includes(segments[0])) {
    segments.shift();
  }

  const [firstSegment] = segments;

  switch (firstSegment) {
    case ContentTypePathSegment.news:
      return ContentType.article;
    case ContentTypePathSegment.corporateServices:
      return ContentType.corporateService;
    case ContentTypePathSegment.vacancies:
      return ContentType.corporateService;
    case ContentTypePathSegment.settings:
      return ContentType.systemPage;
    case undefined:
      return ContentType.homepage;
    default:
      return ContentType.contentPage;
  }
};
